import { Avatar } from '@components/avatar';
import { EmptyState } from '@components/empty-state';
import { IconFancyContainer } from '@components/icon-fancy-container';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconCondition } from '@components/icons/condition';
import { IconEdit } from '@components/icons/edit';
import { IconErrorSmall } from '@components/icons/error-small';
import { IconHistory } from '@components/icons/history';
import { IconNotifications } from '@components/icons/notifications';
import { IconPlay } from '@components/icons/play';
import { IconRedo } from '@components/icons/redo';
import { IconRestart } from '@components/icons/restart';
import { IconSkip } from '@components/icons/skip';
import { IconTeam } from '@components/icons/team';
import { Spinner } from '@components/loading-spinner';
import { Horizontal, Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { abbreviateDate } from '@utils/date';

import { useActivityLogs } from 'api/traveller-actions/hooks';
import { ActionActivityEventType } from 'api/types';

import {
  ConnectorLine,
  ContentColumn,
  EventConfig,
  ICON_SIZE,
  IconColumn,
  PerformedByRow,
} from './shared';

const EVENT_CONFIG: Record<ActionActivityEventType, EventConfig> = {
  action_started: { icon: <IconPlay size={ICON_SIZE} />, variant: 'blue' },
  action_completed: { icon: <IconCheckmark size={ICON_SIZE} />, variant: 'green' },
  action_halted: { icon: <IconCondition size={ICON_SIZE} />, variant: 'gray' },
  manually_sent: { icon: <IconTeam size={ICON_SIZE} />, variant: 'indigo' },
  manually_resent: { icon: <IconRedo size={ICON_SIZE} />, variant: 'indigo' },
  skipped: { icon: <IconSkip size={ICON_SIZE} />, variant: 'gray' },
  unskipped: { icon: <IconRedo size={ICON_SIZE} />, variant: 'green' },
  condition_bypassed: { icon: <IconCondition size={ICON_SIZE} />, variant: 'orange' },
  action_edited: { icon: <IconEdit size={ICON_SIZE} />, variant: 'indigo' },
  action_error: { icon: <IconErrorSmall />, variant: 'red' },
  action_reset: { icon: <IconRestart size={ICON_SIZE} />, variant: 'red' },
  reminder_sent: { icon: <IconNotifications size={ICON_SIZE} />, variant: 'green' },
};

const EVENT_LABEL_KEYS: Record<ActionActivityEventType, keyof (typeof locale)['en']> = {
  action_started: 'actionStarted',
  action_completed: 'actionCompleted',
  action_halted: 'actionHalted',
  manually_sent: 'manuallySent',
  manually_resent: 'manuallyResent',
  skipped: 'skipped',
  unskipped: 'unskipped',
  condition_bypassed: 'conditionBypassed',
  action_edited: 'actionEdited',
  action_error: 'actionError',
  action_reset: 'actionReset',
  reminder_sent: 'reminderSent',
};

export const ActivityTimeline = ({ actionId }: { actionId: string }) => {
  const { t } = useScopedTranslate(locale);
  const { data: logs, isLoading } = useActivityLogs(actionId);

  if (isLoading) {
    return <Spinner />;
  }

  if (!logs || logs.length === 0) {
    return (
      <EmptyState
        variant="small"
        icon={<IconHistory />}
        header={t('noActivity')}
        content={t('noActivityDescription')}
        skipMargin
      />
    );
  }

  return (
    <Stack>
      {[...logs].map((log, index) => {
        const eventType = log.eventType as ActionActivityEventType;
        const config = EVENT_CONFIG[eventType];
        const isLast = index === logs.length - 1;

        return (
          <Horizontal key={log.shortId} space={3}>
            <IconColumn>
              <IconFancyContainer icon={config.icon} variant={config.variant} size="sm" />
              {!isLast && <ConnectorLine />}
            </IconColumn>
            <ContentColumn>
              <Text font="semibold14" text={t(EVENT_LABEL_KEYS[eventType])} />
              {log.performedBy && (
                <PerformedByRow>
                  <Avatar
                    name={log.performedBy.name}
                    src={log.performedBy.picture ?? undefined}
                    size="sm"
                  />
                  <Text font="regular12" color="textSecondary" text={log.performedBy.name} />
                </PerformedByRow>
              )}
              <Text
                font="regular12"
                color="textHelper"
                text={abbreviateDate(log.dateCreated, 'en', 'datetime')}
              />
            </ContentColumn>
          </Horizontal>
        );
      })}
    </Stack>
  );
};

const locale = {
  en: {
    actionStarted: 'Action started',
    actionCompleted: 'Action completed',
    actionHalted: 'Halted by condition',
    manuallySent: 'Manually started',
    manuallyResent: 'Manually resent',
    skipped: 'Action skipped',
    unskipped: 'Action unskipped',
    conditionBypassed: 'Condition bypassed',
    actionEdited: 'Action edited',
    actionError: 'Action error',
    actionReset: 'Action reset',
    reminderSent: 'Reminder sent',
    noActivity: 'No activity',
    noActivityDescription: 'Action has not been started or no manual edits have been made',
  },
};
